import React, { useRef, useState, useContext, useEffect, useCallback } from 'react';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { store } from 'components/Store';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import normalize from 'components/Dashboard/utilities/normalizeString';
import changeItemNamesPascalCase from './Helpers/changeItemsPascalCase';
import useCreateResourcesName from './Helpers/createResourcesName';
import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import { useSiteSectionsNames } from 'utils/getAppSite';
import ConfigData from 'config/ConfigData';

const Resources = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activeSubscriptions, activePlan } = state;
  const { planCode } = activeSubscriptions;
  const {
    resources,
    typeAlert,
    allow,
    referenceValues,
    adminSubscriptions,
    scrollPosition,
    includeIcon,
    closeOnScroll,
    scrollRef = {},
    embedSettings,
    idPrefix,
  } = props;
  const enableTwitter =
    adminSubscriptions?.enableTwitter ?? activeSubscriptions?.enableTwitter ?? activePlan?.enableTwitter;
  const enableLiveTranscription =
    adminSubscriptions?.enableLiveTranscription ??
    activeSubscriptions?.enableLiveTranscription ??
    activePlan?.enableLiveTranscription;
  const showTweets = import.meta.env.VITE_APP_ENABLE_TWITTER === 'true';
  const buttonRef = useRef(null);

  let initialContent =
    referenceValues ??
    (parliamentarianUsers(planCode)
      ? ConfigData.initialContentResources?.filter((item) => item !== 'Lobbying material')
      : ConfigData.initialContentResources);
  let createContent = () => {
    if (showTweets && !initialContent.includes('Tweets')) {
      return [...initialContent, 'Tweets'];
    } else {
      return initialContent;
    }
  };
  const { createResourcesName } = useCreateResourcesName();
  let content = createContent();
  const [showResources, setShowResources] = useState(false);
  const resourcesElement = useRef(null);

  const ImmediateWarningText = `Questions & answers ${
    showTweets ? 'and X posts ' : ''
  }often have a lot activity.<br/> Setting immediate alerts for ${showTweets ? 'these' : 'this'} resource${
    showTweets ? 's' : ''
  }<br/> may result in frequent notifications`;
  const hideResources = () => {
    setShowResources(false);
  };

  useOutsideElement(resourcesElement, hideResources);
  const [dropdownDimentions, setDropdownDimentions] = useState({ top: 0, width: 0 });
  const [containerHeight, setContainerHeight] = useState(0);

  const createDimentions = () => {
    let node = resourcesElement.current;
    if (node !== null) {
      const { top, height, width } = node.getBoundingClientRect() ?? {};
      const topValue = top + height - 2;
      const dropdownHeight = 200;
      const paddingValue = 40;
      const notSpaceAtBottom = dropdownHeight + topValue + paddingValue > window.innerHeight;
      setDropdownDimentions({
        top: topValue,
        width: width,
        maxHeight: notSpaceAtBottom ? top - 28 : window.innerHeight - topValue - 28,
      });
    }
  };

  useEffect(() => {
    //AE: To wait the popup appears and get the correct position
    setTimeout(() => {
      createDimentions();
    }, 300);
    window.addEventListener('resize', createDimentions);
    return () => {
      window.removeEventListener('resize', createDimentions);
    };
  }, [resourcesElement.current, typeAlert, resources]);

  useEffect(() => {
    if (scrollPosition) {
      if (closeOnScroll) {
        setShowResources(false);
      }
      createDimentions();
    }
  }, [scrollPosition]);

  useEffect(() => {
    const createFixed = () => {
      setShowResources(false);
      createDimentions();
    };
    scrollRef.current?.addEventListener('scroll', createFixed);
    return () => {
      if (scrollRef.current) {
        scrollRef.current.removeEventListener('scroll', createFixed);
      }
    };
  }, [scrollRef.current]);

  const warningResources = ['AnsweredWrittenQuestion', 'UnansweredWrittenQuestion', 'Tweets', 'Retweets'];
  const warningConditionalClass =
    typeAlert === 'Immediate' &&
    warningResources.some((resource) => resources.includes(resource)) &&
    !showResources &&
    allow;

  const topCondition = dropdownDimentions.top + dropdownDimentions.maxHeight > window.innerHeight;
  const getDropdownStyles = () => {
    let styleObj = {
      position: 'fixed',
      width: `${dropdownDimentions.width}px`,
      top: `${dropdownDimentions.top}px`,
    };
    if (topCondition) {
      styleObj = {
        ...styleObj,
        top: `${
          dropdownDimentions.top - containerHeight - resourcesElement?.current?.getBoundingClientRect()?.height + 3
        }px`,
      };
    }
    return styleObj;
  };

  const resourcesContainer = useCallback((node) => {
    if (node !== null) {
      const { height } = node.getBoundingClientRect() ?? {};
      setContainerHeight(height);
    }
  }, []);

  return (
    <>
      <div className={`position-relative ${showResources ? 'dropdown-open z-max' : ''}`} ref={resourcesElement}>
        <button
          className={`general-button pl-3 pr-4 mb-0 hour-select d-block col-16 text-left
        ${allow ? '' : 'disabled-hour-select'} ${
          showResources
            ? `hour-select-opendropdown ${
                dropdownDimentions.top + dropdownDimentions.maxHeight > window.innerHeight
                  ? 'opened-dropdown-at-top'
                  : ''
              }`
            : ''
        }
      `}
          onClick={() => {
            if (allow) {
              setShowResources(!showResources);
            }
          }}
          ref={buttonRef}
        >
          {includeIcon && (
            <span className='d-inline-block' style={{ marginTop: '1px', marginLeft: '1px' }}>
              <span
                className='icon-key-update mr-2 paragraph-p3 d-inline-block position-relative'
                style={{ verticalAlign: 'middle', top: '-1px' }}
              />
            </span>
          )}
          {createResourcesName(resources.join(', '), referenceValues, planCode)}
        </button>

        {showResources && (
          <>
            <div className='general-close-background' onClick={() => setShowResources(false)}></div>
            <div
              className={`resources-dropdown p-0 rounded-${topCondition ? 'top' : 'bottom'} ${
                topCondition ? 'open-top' : ''
              }`}
              style={getDropdownStyles()}
              ref={resourcesContainer}
            >
              <ul
                className={`policy-list scroll-container hover-container mb-0 resources-dropdown-list ${
                  content?.length === 1 ? 'pb-0' : ''
                }`}
                style={{
                  height: 'auto',
                  maxHeight: `${dropdownDimentions.maxHeight}px`,
                }}
              >
                <div className='hover-content pl-2'>
                  <ResourcesItems
                    {...props}
                    content={content}
                    enableTwitter={enableTwitter}
                    enableLiveTranscription={enableLiveTranscription}
                    ImmediateWarningText={ImmediateWarningText}
                    embedSettings={embedSettings}
                    idPrefix={idPrefix}
                  />
                </div>
              </ul>
            </div>
          </>
        )}
        {warningConditionalClass && (
          <p className='paragraph-p1 text-danger-red-100 mt-2'>
            Q&As and X posts often have a lot of activity. Setting immediate alerts for these resources may result in
            frequent notifications.
          </p>
        )}
      </div>
      <ReactTooltip
        id={'resources-tooltip'}
        effect={'solid'}
        type={'info'}
        className={'pricing-feature-tooltip'}
        backgroundColor={'#00122B'}
        textColor={'#fff'}
        multiline={true}
        arrowColor={'transparent'}
        place={'right'}
      />
    </>
  );
};

const ResourcesItems = (props) => {
  const {
    content,
    enableTwitter,
    enableLiveTranscription,
    resources,
    changeResources,
    typeAlert,
    ImmediateWarningText,
    showRetweets,
    embedSettings,
    idPrefix = 'resources',
  } = props;
  const { getName } = useSiteSectionsNames();
  const immediateAlert = typeAlert === 'Immediate';
  const warningConditional = (item) => {
    const newlyTabledWrittenQuestions =
      resources.includes('UnansweredWrittenQuestion') && item === 'Newly tabled written questions';
    const answeredWrittenQuestions =
      resources.includes('AnsweredWrittenQuestion') && item === 'Answered written questions';
    const tweets = resources.includes('Tweets') && item === 'Tweets';
    return immediateAlert && (newlyTabledWrittenQuestions || answeredWrittenQuestions || tweets);
  };

  const checkItem = (name, index) => {
    const applyResources = () => {
      let newResources = resources.slice();
      if (resources.includes(name)) {
        if (newResources.length === 1) {
          createNotification('danger', `You must have at least 1 resource selected`);
        } else {
          let position = newResources.indexOf(name);
          newResources.splice(position, 1);
          if (name === 'Tweets' && newResources.includes('Retweets')) {
            let retweetsPosition = newResources.indexOf('Retweets');
            newResources.splice(retweetsPosition, 1);
          }
          changeResources(newResources);
        }
      } else {
        newResources.splice(index, 0, name);
        changeResources(newResources);
      }
    };
    if (name !== 'Tweets' || (name === 'Tweets' && (enableTwitter || resources.includes('Tweets')))) {
      applyResources();
    }
  };

  return (
    <>
      {content.map((item, index) => {
        const namePascalCase = changeItemNamesPascalCase(item);
        const disabledItem =
          (item === 'Parliament live' && ((!embedSettings && !immediateAlert) || !enableLiveTranscription)) ||
          ((item === 'Tweets' || item === 'Retweets') && !enableTwitter);

        return (
          <div key={`resource-${item}`}>
            <li className={`checkbox-item ${disabledItem ? 'checkbox-item-disabled' : ''}`}>
              <input
                id={`${idPrefix}-${normalize(item)}`}
                onChange={() => {
                  checkItem(namePascalCase, index);
                }}
                type='checkbox'
                disabled={disabledItem}
                checked={resources.includes(namePascalCase)}
              />
              <label className={`${disabledItem ? 'light-60-text' : ''}`} htmlFor={`${idPrefix}-${normalize(item)}`}>
                {getName({ property: item })}
                {warningConditional(item) && <WarningElement ImmediateWarningText={ImmediateWarningText} />}
                {((item === 'Tweets' && !enableTwitter) ||
                  (item === 'Parliament live' && !enableLiveTranscription)) && (
                  <span
                    className='icon-padlock d-inline-block-centered ml-2 centered-3'
                    data-tooltip-html={
                      item === 'Tweets'
                        ? 'Coverage of policymakers on X<br /> is available as an add-on.<br />Please contact us to enable it'
                        : 'Parliament live is available on Mogul'
                    }
                    data-tooltip-id='resources-tooltip'
                  />
                )}
                {item === 'Parliament live' && enableLiveTranscription && !embedSettings && (
                  <span
                    data-tooltip-class-name={'general-tooltip pricing-feature-tooltip report-popup-tooltip'}
                    data-tooltip-content={
                      immediateAlert
                        ? `If enabled, you'll receive alerts within 15 minutes of any relevant remark made in Parliament. Alerts are grouped every 15 minutes, but you can view them in real-time on the Monitor tab of the platform`
                        : 'Available for immediate alerts only'
                    }
                    data-tooltip-id='resources-tooltip'
                    className='icon-info d-inline-block-centered centered-3 ml-2 paragraph-p3'
                  />
                )}
              </label>
            </li>
            {item === 'Tweets' && showRetweets && (
              <div className='pl-4'>
                <div className={`checkbox-item`}>
                  <input
                    id={'retweets-0'}
                    onChange={() => {
                      if (enableTwitter || resources.includes('Tweets')) {
                        let newResources = resources.slice();
                        if (resources.includes('Retweets')) {
                          let position = newResources.indexOf('Retweets');
                          newResources.splice(position, 1);
                          changeResources(newResources);
                        } else {
                          newResources.push('Retweets');
                          if (!newResources.includes('Tweets')) {
                            newResources.push('Tweets');
                          }
                          changeResources(newResources);
                        }
                      }
                    }}
                    type='checkbox'
                    checked={resources.includes('Retweets')}
                  />
                  <label htmlFor={'retweets-0'} className={`${disabledItem ? 'light-60-text' : ''}`}>
                    X reposts
                    {((item === 'Tweets' && !enableTwitter) ||
                      (item === 'Parliament live' && !enableLiveTranscription)) && (
                      <span
                        className='icon-padlock d-inline-block-centered ml-2 centered-3'
                        data-tooltip-html={
                          item === 'Tweets'
                            ? 'Coverage of policymakers on X<br /> is available as an add-on.<br />Please contact us to enable it'
                            : 'Parliament live is available on Mogul'
                        }
                        data-tooltip-id='resources-tooltip'
                      />
                    )}
                  </label>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

const WarningElement = (props) => {
  const { ImmediateWarningText } = props;
  return (
    <>
      <span
        className='warning-resource icon-warning-circle-thick title-h4 text-danger specific-left-margin-5'
        data-tooltip-html={ImmediateWarningText}
        data-tooltip-id='resources-tooltip'
        data-tooltip-position-strategy={'fixed'}
      />
    </>
  );
};

export default Resources;
